import { now, ZonedDateTime } from '@internationalized/date';
import {
  OrSettingsEnum,
  SuggestedSettingsType,
  SuggestedSettingsEnum,
  SuggestedSettingsKeyValueType,
} from '@lp/features/RoutePlanner/types';
import { PrimaryLocationIds } from '@lp/features/RoutePlanner/utils/case-locations';
import { RouteTypeSettingsEnum, TravelTypeHosTx } from '@/api/apis/mission-planner';
import { AviaLegTypes, GroundLegTypes } from '@/api/apis/route';
import { CaseFacility, ServiceType } from '@/api/apis/case';
import { StrictMap } from '@/utils/helpers';
import { CUSTOM_ROUTE_SETTINGS, OR_MAX_OFFSET_HOURS } from '../config';
import { getSegmentValidity } from './route-types';

export const getSettingsListByRouteType = (
  routeType: RouteTypeSettingsEnum,
  serviceType: ServiceType,
): SuggestedSettingsEnum[] => {
  if (routeType === RouteTypeSettingsEnum.Custom_DH_TXC_Hub)
    return [...CUSTOM_ROUTE_SETTINGS, SuggestedSettingsEnum.OrganTransportationType];

  if (routeType === RouteTypeSettingsEnum.Custom_TXC_Hub) return CUSTOM_ROUTE_SETTINGS;

  const isNopWithTmdxSurgeon = serviceType === ServiceType.NopWithTmdxSurgeon;
  const rsSettings = isNopWithTmdxSurgeon ? [SuggestedSettingsEnum.RS] : [];

  if (routeType === RouteTypeSettingsEnum.Hub_DH) {
    return [
      SuggestedSettingsEnum.RouteType,
      SuggestedSettingsEnum.OR,
      SuggestedSettingsEnum.CS,
      ...rsSettings,
      SuggestedSettingsEnum.DeploymentHub,
    ];
  }

  return [
    SuggestedSettingsEnum.RouteType,
    SuggestedSettingsEnum.OR,
    SuggestedSettingsEnum.CS,
    ...rsSettings,
    SuggestedSettingsEnum.DeploymentHub,
    SuggestedSettingsEnum.OrganTransportationType,
  ];
};

const DEFAULT_START_TIME_OFFSET_HOURS = 1;

export const getSettingsDefaultValue = (item: SuggestedSettingsEnum, donorOr: ZonedDateTime) => {
  switch (item) {
    case SuggestedSettingsEnum.OrganTransportationType:
      return TravelTypeHosTx.Default;
    case SuggestedSettingsEnum.StartTime:
      return now('UTC').add({ hours: DEFAULT_START_TIME_OFFSET_HOURS });
    case SuggestedSettingsEnum.OR:
      return {
        type: OrSettingsEnum.OrRange,
        offset: OR_MAX_OFFSET_HOURS,
        selectedOr: donorOr,
      };
    case SuggestedSettingsEnum.RouteType:
      return RouteTypeSettingsEnum.Full;
    default:
      return null;
  }
};

const getDefaultRouteType = (
  legs: (AviaLegTypes | GroundLegTypes)[],
  opoRequest: boolean,
  reAllocationCase: boolean,
  txCenterDetails: CaseFacility | null,
  primary?: PrimaryLocationIds,
): RouteTypeSettingsEnum => {
  if (opoRequest && !txCenterDetails) return RouteTypeSettingsEnum.Hub_DH;
  if (reAllocationCase) return RouteTypeSettingsEnum.Custom_TXC_Hub;

  const { isHubDHSegmentValid, hasFullRouteBuilt } = getSegmentValidity(legs, primary);

  if (opoRequest) return isHubDHSegmentValid ? RouteTypeSettingsEnum.Custom_TXC_Hub : RouteTypeSettingsEnum.Full;

  return hasFullRouteBuilt ? RouteTypeSettingsEnum.Custom_TXC_Hub : RouteTypeSettingsEnum.Full;
};

export const getDefaultSuggestedSettings = (
  legs: (AviaLegTypes | GroundLegTypes)[],
  serviceType: ServiceType,
  opoRequest: boolean,
  reAllocationCase: boolean,
  txCenterDetails: CaseFacility | null,
  orDateTime: ZonedDateTime,
  primary?: PrimaryLocationIds,
): StrictMap<SuggestedSettingsKeyValueType> => {
  const settingsMap = new StrictMap<SuggestedSettingsKeyValueType>();

  const routeType = getDefaultRouteType(legs, opoRequest, reAllocationCase, txCenterDetails, primary);
  const settingsList = getSettingsListByRouteType(routeType, serviceType);

  settingsList.forEach((key) => settingsMap.set(key, getSettingsDefaultValue(key, orDateTime)));
  settingsMap.set(SuggestedSettingsEnum.RouteType, routeType);

  return settingsMap;
};

export const convertSettingsListToMap = (
  settings: SuggestedSettingsType[],
): StrictMap<SuggestedSettingsKeyValueType> => {
  const settingsMap = new StrictMap<SuggestedSettingsKeyValueType>();

  settings.forEach((item) => {
    settingsMap.set(item.type, item.value);
  });

  return settingsMap;
};

export const getSettingsPreviousValue = (
  map: StrictMap<SuggestedSettingsKeyValueType>,
  key: SuggestedSettingsEnum,
  caseOrTime: ZonedDateTime,
  restrictedStartLocationIds: Set<string> = new Set(),
): SuggestedSettingsKeyValueType[keyof SuggestedSettingsKeyValueType] => {
  if (key === SuggestedSettingsEnum.StartLocation) {
    const currentSettingsData = map.get(key);
    const isDefaultValueRequired = !!currentSettingsData && restrictedStartLocationIds.has(currentSettingsData.id);

    return isDefaultValueRequired
      ? getSettingsDefaultValue(key, caseOrTime)
      : (map.get(key) as SuggestedSettingsKeyValueType[keyof SuggestedSettingsKeyValueType]);
  }

  return map.get(key) as SuggestedSettingsKeyValueType[keyof SuggestedSettingsKeyValueType];
};
